import { supabase } from '../supabase/client';
import type { GraphicsQuestion, ExamAnswer, AutoSaveRecord, ExamSubmitted } from '../types/graphics';
import { getBeijingTimestamp } from './timeUtils';

export const graphicsStorage = {
  async getQuestions(): Promise<GraphicsQuestion[]> {
    const { data, error } = await supabase
      .from('graphics_answer_examples')
      .select('*')
      .order('created_at', { ascending: false });
    if (error) {
      
      return [];
    }
    return (data || []) as any;
  },

  async saveQuestion(question: GraphicsQuestion): Promise<boolean> {
    const { error } = await supabase
      .from('graphics_answer_examples')
      .upsert({
        id: question.id,
        content: question.content,
        updated_at: getBeijingTimestamp()
      });
    if (error) {
      
      return false;
    }
    return true;
  },

  async deleteQuestion(id: string): Promise<void> {
    await supabase.from('graphics_answer_examples').delete().eq('id', id);
  },

  async getQuestionById(id: string): Promise<GraphicsQuestion | null> {
    const { data, error } = await supabase
      .from('graphics_answer_examples')
      .select('*')
      .eq('id', id)
      .maybeSingle();
    if (error || !data) return null;
    return data as any;
  },

  async getAnswers(examId: string, userId: string): Promise<ExamAnswer> {
    const { data, error } = await supabase
      .from('exam_answers')
      .select('*')
      .eq('exam_id', examId)
      .eq('user_id', userId);
    if (error || !data) return {};
    const result: ExamAnswer = {};
    data.forEach((row: any) => {
      if (!result[row.question_id]) result[row.question_id] = {};
      result[row.question_id][row.item_id || 'default'] = row.answers || [];
    });
    return result;
  },

  async saveAnswer(examId: string, userId: string, questionId: string, itemId: string, images: string[]): Promise<void> {
    await supabase.from('exam_answers').upsert({
      exam_id: examId,
      user_id: userId,
      question_id: questionId,
      item_id: itemId,
      answers: images,
      updated_at: getBeijingTimestamp()
    });
  },

  async saveAllAnswers(examId: string, userId: string, answers: ExamAnswer): Promise<void> {
    const records: any[] = [];
    Object.entries(answers).forEach(([questionId, items]) => {
      Object.entries(items).forEach(([itemId, images]) => {
        records.push({
          exam_id: examId,
          user_id: userId,
          question_id: questionId,
          item_id: itemId,
          answers: images,
          updated_at: getBeijingTimestamp()
        });
      });
    });
    if (records.length > 0) {
      await supabase.from('exam_answers').upsert(records);
    }
  },

  async autoSave(examId: string, userId: string, answers: ExamAnswer): Promise<void> {
    await supabase.from('exam_auto_save').upsert({
      exam_id: examId,
      user_id: userId,
      answers: answers,
      timestamp: Date.now(),
      updated_at: getBeijingTimestamp()
    });
  },

  async getAutoSave(examId: string, userId: string): Promise<AutoSaveRecord | null> {
    const { data, error } = await supabase
      .from('exam_auto_save')
      .select('*')
      .eq('exam_id', examId)
      .eq('user_id', userId)
      .maybeSingle();
    if (error || !data) return null;
    return {
      timestamp: data.timestamp,
      answers: data.answers as any
    };
  },

  async markSubmitted(examId: string, userId: string, answers: ExamAnswer): Promise<void> {
    await supabase.from('exam_submissions').upsert({
      exam_id: examId,
      user_id: userId,
      submitted: true,
      submitted_at: getBeijingTimestamp(),
      answers: answers
    });
  },

  async isSubmitted(examId: string, userId: string): Promise<boolean> {
    const { data, error } = await supabase
      .from('exam_submissions')
      .select('submitted')
      .eq('exam_id', examId)
      .eq('user_id', userId)
      .maybeSingle();
    if (error || !data) return false;
    return data.submitted;
  },

  async getSubmittedAnswers(examId: string, userId: string): Promise<ExamSubmitted | null> {
    const { data, error } = await supabase
      .from('exam_submissions')
      .select('*')
      .eq('exam_id', examId)
      .eq('user_id', userId)
      .maybeSingle();
    if (error || !data) return null;
    return {
      submitted: data.submitted,
      submittedAt: data.submitted_at,
      answers: data.answers as any
    };
  },

  async clearExamData(examId: string, userId: string): Promise<void> {
    await supabase.from('exam_answers').delete().eq('exam_id', examId).eq('user_id', userId);
    await supabase.from('exam_auto_save').delete().eq('exam_id', examId).eq('user_id', userId);
  },

  isStorageAvailable(): boolean {
    try {
      const test = '__storage_test__';
      localStorage.setItem(test, test);
      localStorage.removeItem(test);
      return true;
    } catch (e) {
      return false;
    }
  }
};
